import React, { useState, useEffect } from 'react';
import { notificationApi } from '../services/api';
import { Card, Button, Table, Alert, Form, Row, Col, Modal } from 'react-bootstrap';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchNotifications();
  }, [statusFilter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = {};

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await notificationApi.getAll(params);
      setNotifications(response.data.data.notifications);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSendTestNotification = async () => {
    try {
      await notificationApi.sendTest(testEmail);
      alert('Test notification sent successfully!');
      setShowTestModal(false);
      setTestEmail('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send test notification');
    }
  };

  if (loading) {
    return <div>Loading notifications...</div>;
  }

  return (
    <div className="notifications-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Notifications Management</h2>
        <Button variant="primary" onClick={() => setShowTestModal(true)}>
          Send Test Notification
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select value={statusFilter} onChange={handleStatusChange}>
                  <option value="all">All Notifications</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Type</th>
                <th>Recipient</th>
                <th>Subject</th>
                <th>Scheduled Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notifications.length > 0 ? (
                notifications.map(notification => (
                  <tr key={notification._id}>
                    <td>{notification.notificationType.replace('_', ' ')}</td>
                    <td>
                      <div>{notification.recipientEmail}</div>
                      {notification.recipientPhone && (
                        <div className="text-muted">{notification.recipientPhone}</div>
                      )}
                    </td>
                    <td>{notification.subject}</td>
                    <td>{new Date(notification.sendAt).toLocaleString()}</td>
                    <td>
                      <span className={`badge bg-${getStatusBadge(notification.status)}`}>
                        {notification.status}
                      </span>
                    </td>
                    <td>
                      <Button variant="outline-info" size="sm" className="me-2">
                        View
                      </Button>
                      {notification.status === 'pending' && (
                        <Button variant="outline-danger" size="sm">
                          Cancel
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">No notifications found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showTestModal} onHide={() => setShowTestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Send Test Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSendTestNotification}>
            Send Test
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

function getStatusBadge(status) {
  switch (status) {
    case 'pending': return 'warning';
    case 'sent': return 'success';
    case 'failed': return 'danger';
    default: return 'secondary';
  }
}

export default NotificationsPage;
